import {
  Avatar,
  Chip,
  IconButton,
  Rating,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment";
import React, { useState } from "react";
import appcolors from "../../constants/Colors";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  CURRENCY,
  DEFAULT_VAT_PERCENTAGE,
  INVENTORY_MOVEMENT_REASON,
  PACKAGE_USAGE_TYPE,
} from "../../constants/Variables";
import {
  calculateProductOrderVatAmount,
  calculateSupplierTotalOrderPrice,
  calculateVat,
  roundTo2Decimal,
} from "../../utils";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const tableStyle = {
  "&.MuiTableContainer-root .MuiTableHead-root": {
    backgroundColor: appcolors.primary,
  },
  "&.MuiTableContainer-root .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root":
    {
      padding: "6px 16px",
      color: appcolors.white,
      fontSize: "1rem",
    },
};

export default function DynamicPaginatedTable({
  tableWidth,
  minWidth,
  tableHeader,
  tableData,
  displayRows,
  paginatedData,
  handleViewDetail,
  showDynamicPagination = false,
  rowsPerPageOptions = [10, 20, 30, 50, 100],
  onPageChange,
  onRowsPerPageChange,
  isLoading = false,
  handleViewPricingDetails,
  onViewOrderDetail,
  onUpdateVerification,
  onUpdateStatus,
  onEdit,
  onDelete,
  stickyActions = false,
}) {
  // Translation Hook
  const { t } = useTranslation();
  // Page State
  const [page, setPage] = useState(0);
  // Rows Per Page State
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Handle Page Change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  // Current Language
  const { currentLanguage } = useSelector((state) => state.translationReducer);
  // Get Settings from Redux
  const settings = useSelector((state) => state.settingsReducer.settings);

  // Visible Rows
  const visibleRows =
    (rowsPerPage && paginatedData === undefined) > 0
      ? tableData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : tableData;
  const skeletonRowCount = rowsPerPage || 10;

  // Handle Rows Per Page Change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getInventoryMovementReasonColor = (reason) => {
    if (reason === "stock_in") return "green";
    if (reason === "stock_out") return "red";
    if (reason === "release") return "blue";
    if (reason === "adjustment") return "orange";
    return "gray";
  };

  return (
    <React.Fragment key="dynamic-paginated-table">
      <TableContainer sx={tableStyle}>
        <Table
          sx={{
            width: tableWidth ? tableWidth : "100%",
            minWidth: minWidth ? minWidth : "900px",
          }}
        >
          <TableHead>
            <TableRow>
              {tableHeader?.map((header) => (
                <TableCell key={header}>{t(header)}</TableCell>
              ))}
              {stickyActions && (
                <TableCell
                  sx={{
                    position: "sticky",
                    right: 0,
                    zIndex: 3,
                    px: 0,
                    backgroundColor: "primary.main",
                  }}
                >
                  {t("common.actions")}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
                <TableRow key={`skeleton-row-${rowIndex}`}>
                  {displayRows?.map((val, cellIndex) => (
                    <TableCell key={`${val}-${cellIndex}`}>
                      <Skeleton animation="wave" height={24} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : visibleRows?.length === 0 ? (
              <TableRow>
                <TableCell align="center" colSpan={tableHeader?.length || 1}>
                  <Typography fontWeight={500}>{t("noDataFound")}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              visibleRows?.map((row, index) => {
                const discountAmount = row?.discount?.amount || 0;
                const discountVat = calculateVat(discountAmount, row?.vat);

                // Calculate Order Sub Total
                const orderSubTotal =
                  calculateSupplierTotalOrderPrice(row?.order || []) -
                  discountAmount;

                // Calculate Order Vat Amount
                const orderVatAmount =
                  calculateProductOrderVatAmount(row?.order || []) -
                  discountVat;

                // Calculate Final Order Total
                const finalOrderTotal = roundTo2Decimal(
                  orderSubTotal + orderVatAmount,
                );

                // This is calculated for the base packages
                const finalPackagePrice = row.effectivePackagePrice;

                // This is calculated for the base packages
                const packageVat = calculateVat(
                  finalPackagePrice,
                  settings?.tax || DEFAULT_VAT_PERCENTAGE,
                );

                // This is calculated for the base packages
                const finalPriceAfterVat = roundTo2Decimal(
                  finalPackagePrice + packageVat,
                );

                return (
                  <TableRow key={index}>
                    {displayRows.map((val) => (
                      <React.Fragment key={val}>
                        {val === "created_date" ? (
                          <TableCell>
                            {moment(row.createdAt).format("DD/MM/YYYY")}
                            <br />
                            {moment(row.createdAt).format("hh:mm:ss a")}
                          </TableCell>
                        ) : val === "user_image_name" ? (
                          <TableCell>
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={1}
                            >
                              <Avatar src={row.profileImg} />
                              <Typography>
                                {`${row.firstname} ${row.lastname}`}
                              </Typography>
                            </Stack>
                          </TableCell>
                        ) : val === "order_detail" ? (
                          <TableCell>
                            <Chip
                              icon={<RemoveRedEyeIcon />}
                              label={"View Detail"}
                              onClick={() => handleViewDetail(row, index)}
                            />
                          </TableCell>
                        ) : val === "inventory_product_detail" ? (
                          <TableCell>
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={1}
                              width={"400px"}
                            >
                              <Avatar
                                src={row?.productData?.coverImage}
                                sx={{
                                  height: "40px",
                                  width: "40px",
                                  backgroundColor: "#edebeb",
                                  border: "1px solid #edebeb",
                                  borderRadius: "6px",
                                }}
                              />
                              <Stack>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    display: "-webkit-box",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: "400px",
                                  }}
                                >
                                  {row?.productData?.name[currentLanguage]}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color={"text.secondary"}
                                >
                                  {t("sku")}: {row?.sku}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                        ) : val === "inventory_movement_reason" ? (
                          <TableCell>
                            <Chip
                              label={
                                INVENTORY_MOVEMENT_REASON.find(
                                  (val) => val.value === row?.reason,
                                )?.label[currentLanguage]
                              }
                              variant="filled"
                              size="small"
                              sx={{
                                textTransform: "capitalize",
                                color: "custom.white",
                                backgroundColor:
                                  getInventoryMovementReasonColor(row?.reason),
                              }}
                            />
                          </TableCell>
                        ) : val === "inventory_quantity_change" ? (
                          <TableCell>
                            <Typography variant="body2">
                              {row?.quantityChange}
                            </Typography>
                          </TableCell>
                        ) : val === "inventory_previous_quantity" ? (
                          <TableCell>
                            <Typography variant="body2">
                              {row?.previousQuantity}
                            </Typography>
                          </TableCell>
                        ) : val === "inventory_current_quantity" ? (
                          <TableCell>
                            <Typography variant="body2">
                              {row?.currentQuantity}
                            </Typography>
                          </TableCell>
                        ) : val === "inventory_pricing_details" ? (
                          <TableCell>
                            <Chip
                              variant="filled"
                              label={t("view")}
                              clickable
                              onClick={() =>
                                handleViewPricingDetails(row, index)
                              }
                              size="small"
                              sx={{
                                textTransform: "capitalize",
                                color: "custom.white",
                                backgroundColor: "primary.main",
                                borderRadius: "6px",
                                "&:hover": {
                                  backgroundColor: "primary.dark",
                                },
                              }}
                            />
                          </TableCell>
                        ) : val === "order_id" ? (
                          <TableCell>
                            <Typography variant={"body2"} fontWeight={500}>
                              {row?.orderId}
                            </Typography>
                          </TableCell>
                        ) : val === "order_user_name" ? (
                          <TableCell>{`${row?.user.firstname} ${row?.user.lastname}`}</TableCell>
                        ) : val === "order_sub_total" ? (
                          <TableCell>{` ${orderSubTotal} ${CURRENCY}`}</TableCell>
                        ) : val === "order_vat" ? (
                          <TableCell>{row?.vat}%</TableCell>
                        ) : val === "order_vat_amount" ? (
                          <TableCell>{`${orderVatAmount.toFixed(2)} ${CURRENCY}`}</TableCell>
                        ) : val === "order_final_total" ? (
                          <TableCell>{`${finalOrderTotal} ${CURRENCY}`}</TableCell>
                        ) : // Package Table Data
                        val === "package_name_image" ? (
                          <TableCell>
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={1}
                              width={"400px"}
                            >
                              <Avatar
                                src={row.coverImage}
                                sx={{
                                  height: 55,
                                  width: 55,
                                  borderRadius: "6px",
                                  backgroundColor: "#edebeb",
                                  border: "1px solid #edebeb",
                                }}
                              />
                              <Tooltip
                                title={row.name?.[currentLanguage] || ""}
                                placement="top"
                              >
                                <Typography
                                  variant={"body1"}
                                  textTransform={"capitalize"}
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    maxWidth: "100%",
                                  }}
                                >
                                  {row.name[currentLanguage]}
                                </Typography>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        ) : val === "package_solution_type" ? (
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {PACKAGE_USAGE_TYPE.find((item) =>
                              row?.solutionType?.includes(item.value),
                            )?.label?.[currentLanguage] || (
                              <Typography variant="body2" color="info.main">
                                {t("notAddedYet")}
                              </Typography>
                            )}
                          </TableCell>
                        ) : val === "package_category_name" ? (
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {row?.category?.name?.[currentLanguage]}
                          </TableCell>
                        ) : val === "package_sub_category_name" ? (
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {row?.subCategory?.length > 0 ? (
                              row?.subCategory.map((x, idx) => (
                                <Typography
                                  key={idx}
                                  variant="body2"
                                  mb={0.5}
                                  component="span"
                                >
                                  {x?.name?.[currentLanguage]}
                                  {idx !== row.subCategory.length - 1
                                    ? ", "
                                    : ""}
                                </Typography>
                              ))
                            ) : (
                              <Typography variant="body2" color="info.main">
                                {t("notAddedYet")}
                              </Typography>
                            )}
                          </TableCell>
                        ) : val === "package_sub_sub_category_name" ? (
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {row?.subSubCategory?.length > 0 ? (
                              row?.subSubCategory.map((x, idx) => (
                                <Typography
                                  key={idx}
                                  variant="body2"
                                  mb={0.5}
                                  component="span"
                                >
                                  {x?.name?.[currentLanguage]}
                                  {idx !== row.subSubCategory.length - 1
                                    ? ", "
                                    : ""}
                                </Typography>
                              ))
                            ) : (
                              <Typography variant="body2" color="info.main">
                                {t("notAddedYet")}
                              </Typography>
                            )}
                          </TableCell>
                        ) : val === "package_price" ? (
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {`${roundTo2Decimal(row.packagePrice)} ${CURRENCY}`}
                          </TableCell>
                        ) : val === "package_effective_price" ? (
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {`${roundTo2Decimal(row.effectivePackagePrice)} ${CURRENCY}`}
                          </TableCell>
                        ) : val === "app_vat" ? (
                          <TableCell>
                            {settings?.tax || DEFAULT_VAT_PERCENTAGE}%
                          </TableCell>
                        ) : val === "package_price_after_vat" ? (
                          <TableCell>{`${finalPriceAfterVat} ${CURRENCY}`}</TableCell>
                        ) : val === "package_vat_amount" ? (
                          <TableCell>{`${packageVat} ${CURRENCY}`}</TableCell>
                        ) : val === "rating_in_number" ? (
                          <TableCell>
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              justifyContent={"center"}
                            >
                              <Rating
                                value={row.rating}
                                precision={0.5}
                                readOnly
                                size="small"
                              />

                              <Typography variant="body2">
                                ({row.rating?.toFixed(0) || 0})
                              </Typography>
                            </Stack>
                          </TableCell>
                        ) : val === "package_order_allowed" ? (
                          <TableCell>
                            <Chip
                              size="small"
                              label={
                                row.isAllowedOrder
                                  ? t("allowed")
                                  : t("notAllowed")
                              }
                              color={row.isAllowedOrder ? "success" : "default"}
                              variant={
                                row.isAllowedOrder ? "filled" : "outlined"
                              }
                              sx={{
                                fontWeight: 600,
                                textTransform: "capitalize",
                                "& .MuiChip-label": { px: 1.25 },
                                ...(!row.isAllowedOrder && {
                                  borderColor: "grey.400",
                                  color: "text.secondary",
                                }),
                              }}
                            />
                          </TableCell>
                        ) : val === "package_verified" ? (
                          <TableCell>
                            <Switch
                              checked={row?.isVerified}
                              onChange={(e) =>
                                onUpdateVerification(e.target.checked, row)
                              }
                            />
                          </TableCell>
                        ) : val === "package_status" ? (
                          <TableCell>
                            <Switch
                              checked={row.status}
                              onChange={(e) =>
                                onUpdateStatus(e.target.checked, row)
                              }
                              inputProps={{ "aria-label": "controlled" }}
                            />
                          </TableCell>
                        ) : val === "reviewed_product_detail" ? (
                          <TableCell>
                            <Stack>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Avatar
                                  src={row.productImage}
                                  sx={{
                                    height: 55,
                                    width: 55,
                                    borderRadius: "6px",
                                  }}
                                />
                                <Typography variant="body1">
                                  {row.productName[currentLanguage]}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                        ) : val === "user_details_review" ? (
                          <TableCell>
                            <Stack>
                              <Stack
                                direction={"row"}
                                justifyContent={"space-between"}
                              >
                                <Typography
                                  variant="h3"
                                  fontSize={"1rem"}
                                  fontWeight={600}
                                >
                                  {row.username}
                                </Typography>
                                <Rating
                                  value={row.rating}
                                  precision={0.5}
                                  readOnly
                                  size="small"
                                  color="#7922D8"
                                />
                              </Stack>
                              <Typography variant="body2">
                                {row.comment}
                              </Typography>
                            </Stack>
                          </TableCell>
                        ) : val === "actions" ? (
                          <TableCell
                            sx={{
                              position: "sticky",
                              right: 0,
                              backgroundColor: "secondary.light",
                              zIndex: 2,
                              minWidth: 50,
                              boxShadow: "inset 4px 0 8px rgba(0,0,0,0.08)",
                            }}
                          >
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              justifyContent={"center"}
                            >
                              {onEdit && (
                                <IconButton onClick={() => onEdit(row)}>
                                  <EditIcon
                                    sx={{ fontSize: 18, color: "primary.main" }}
                                  />
                                </IconButton>
                              )}
                              {onDelete && (
                                <IconButton onClick={() => onDelete(row)}>
                                  <DeleteIcon
                                    sx={{ fontSize: 18, color: "error.main" }}
                                  />
                                </IconButton>
                              )}
                            </Stack>
                          </TableCell>
                        ) : (
                          <TableCell>
                            <Typography
                              lineHeight="normal"
                              fontSize={"0.875rem"}
                            >
                              {row[val]}
                            </Typography>
                          </TableCell>
                        )}
                      </React.Fragment>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {showDynamicPagination ? (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={paginatedData.total}
          rowsPerPage={paginatedData.limit}
          page={paginatedData.page - 1}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          labelRowsPerPage={t("rowsPerPage")}
          disabled={isLoading}
        />
      ) : (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={tableData?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t("rowsPerPage")}
        />
      )}
    </React.Fragment>
  );
}
